import React from 'react';
import { Box, Chip, Stack, Tab, Tabs, Typography } from '@mui/material';
import TuningAdminTable, {
  TuningField,
  boolChip,
  safePreview,
} from './TuningAdminTable';
import { useNavigate, useParams } from 'react-router-dom';
import { ADMIN_URL_TOOLBOX_CONFIG } from '../../PageStructure/admin-values';

type PromptSpecTab =
  | 'depth'
  | 'high-level'
  | 'authoritative'
  | 'product-reference'
  | 'discovery';

type Props = {
  visibleTabs?: PromptSpecTab[];
  initialTab?: PromptSpecTab;
};

const activeField: TuningField = {
  name: 'is_active',
  label: 'Active',
  type: 'boolean',
};

const entityTypeOptions = [
  'product',
  'product_component',
  'product_use_case',
  'product_feature',
  'solution_component',
  'go_to_market',
  'security_use_case',
  'security_use_case_component',
].map((v) => ({ label: v, value: v }));

const discoveryTargetTypeOptions = [
  'product_component',
  'product_use_case',
  'security_use_case_component',
].map((v) => ({ label: v, value: v }));

const discoveryParentEntityTypeOptions = [
  'product',
  'security_use_case',
  'go_to_market',
  'solution_component',
].map((v) => ({ label: v, value: v }));

const tabMeta: Array<{
  key: PromptSpecTab;
  label: string;
}> = [
  { key: 'depth', label: 'Depth' },
  { key: 'high-level', label: 'High-Level Guidance' },
  { key: 'authoritative', label: 'Authoritative Usage' },
  { key: 'product-reference', label: 'Product Reference' },
  { key: 'discovery', label: 'Discovery Generation' },
];

const depthFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'name', label: 'Name', required: true },
  { name: 'instructions', label: 'Instructions', type: 'textarea', required: true, minRows: 8 },
  activeField,
];

const highLevelFields: TuningField[] = [
  {
    name: 'entity_type',
    label: 'Entity Type',
    required: true,
    type: 'select',
    options: entityTypeOptions,
  },
  { name: 'guidance', label: 'Guidance', type: 'textarea', required: true, minRows: 10 },
  activeField,
];

const authoritativeFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'name', label: 'Name', required: true },
  {
    name: 'instruction_template',
    label: 'Instruction Template',
    type: 'textarea',
    required: true,
    minRows: 10,
    helperText: 'Supports placeholders like {section_type}, {section_key}, {resource_type}.',
  },
  {
    name: 'conflict_handling',
    label: 'Conflict Handling',
    type: 'textarea',
    minRows: 6,
  },
  activeField,
];

const productReferenceFields: TuningField[] = [
  { name: 'key', label: 'Key', required: true },
  { name: 'name', label: 'Name', required: true },
  {
    name: 'instruction_template',
    label: 'Instruction Template',
    type: 'textarea',
    required: true,
    minRows: 10,
    helperText: 'Supports {product_context}.',
  },
  activeField,
];

const discoveryGenerationFields: TuningField[] = [
  {
    name: 'target_type',
    label: 'Target Type',
    type: 'select',
    required: true,
    options: discoveryTargetTypeOptions,
  },
  {
    name: 'parent_entity_type',
    label: 'Parent Entity Type',
    type: 'select',
    required: true,
    options: discoveryParentEntityTypeOptions,
  },
  { name: 'name', label: 'Name' },
  { name: 'description', label: 'Description', type: 'textarea', minRows: 3 },
  {
    name: 'prompt_template',
    label: 'Prompt Template',
    type: 'textarea',
    required: true,
    minRows: 12,
    helperText:
      'Prompt template used by the discovery generator. Preserve backend-supported placeholders such as {entity_name}, {entity_description}, {parent_entity_type}, {target_type}, and context placeholders.',
  },
  {
    name: 'guidelines',
    label: 'Guidelines',
    type: 'textarea',
    minRows: 8,
    helperText: 'Additional generation rules or selection criteria.',
  },
  {
    name: 'json_contract',
    label: 'JSON Contract',
    type: 'json',
    minRows: 10,
    helperText: 'Expected structured JSON response contract for generated/discovered child entities.',
  },
  { name: 'min_items', label: 'Minimum Items', type: 'number' },
  { name: 'max_items', label: 'Maximum Items', type: 'number' },
  { name: 'sort_order', label: 'Sort Order', type: 'number' },
  activeField,
  { name: 'updated_by', label: 'Updated By' },
];

const PromptSpecsPanel: React.FC<Props> = ({
                                             visibleTabs,
                                             initialTab,
                                           }) => {
  const enabledTabs = React.useMemo(() => {
    if (!visibleTabs?.length) return tabMeta;
    const allowed = new Set(visibleTabs);
    return tabMeta.filter((tab) => allowed.has(tab.key));
  }, [visibleTabs]);

  const { tab4 } = useParams();
  const navigate = useNavigate();
  const getTab = () => {
    if (tab4 && enabledTabs.some(tab => tab.key === tab4)) {
      return tab4;
    }
    if (initialTab && enabledTabs.some((tab) => tab.key === initialTab)) {
      return initialTab;
    }
    return enabledTabs[0]?.key || 'depth';
  }
  const tab = getTab();
  const setTab = (tabName) => navigate(`${ADMIN_URL_TOOLBOX_CONFIG}/ai/tuning/agent/agent-prompt-building-blocks/${tabName}`);

  return (
    <Box>
      {enabledTabs.length > 1 ? (
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2 }}
        >
          {enabledTabs.map((item) => (
            <Tab key={item.key} value={item.key} label={item.label} />
          ))}
        </Tabs>
      ) : null}

      {tab === 'depth' ? (
        <TuningAdminTable
          title="Depth Instructions"
          description="Controls high_level, medium, and detailed prompt depth guidance. Course generation uses these when generation profiles include depth instructions."
          endpoint="db/ai-depth-instruction-spec/"
          queryKey={['ai-depth-instruction-spec']}
          fields={depthFields}
          defaultValues={{
            key: '',
            name: '',
            instructions: '',
            is_active: true,
          }}
          orderBy="key"
          rowTitle={(row) => row.name || row.key}
          rowSubtitle={(row) => safePreview(row.instructions, 180)}
          columns={[
            {
              key: 'name',
              label: 'Depth Level',
              width: 220,
              render: (row) => (
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>
                    {row.name || row.key}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {row.key}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'instructions',
              label: 'Instructions',
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.instructions, 320)}
                </Typography>
              ),
            },
            {
              key: 'is_active',
              label: 'Active',
              width: 100,
              render: (row) => boolChip(row.is_active),
            },
          ]}
        />
      ) : null}

      {tab === 'high-level' ? (
        <TuningAdminTable
          title="High-Level Entity Guidance"
          description="Controls additional guidance for broader entities like product, GTM, product components, product use cases, solution components, and security use cases."
          endpoint="db/ai-high-level-entity-guidance-spec/"
          queryKey={['ai-high-level-entity-guidance-spec']}
          fields={highLevelFields}
          defaultValues={{
            entity_type: 'product',
            guidance: '',
            is_active: true,
          }}
          orderBy="entity_type"
          rowTitle={(row) => row.entity_type}
          rowSubtitle={(row) => safePreview(row.guidance, 180)}
          columns={[
            {
              key: 'entity_type',
              label: 'Entity Type',
              width: 250,
              render: (row) => (
                <Typography variant="body2" fontWeight={700}>
                  {row.entity_type}
                </Typography>
              ),
            },
            {
              key: 'guidance',
              label: 'Guidance',
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.guidance, 320)}
                </Typography>
              ),
            },
            {
              key: 'is_active',
              label: 'Active',
              width: 100,
              render: (row) => boolChip(row.is_active),
            },
          ]}
        />
      ) : null}

      {tab === 'authoritative' ? (
        <TuningAdminTable
          title="Authoritative Resource Usage"
          description="Controls how relation trees, section-primary resources, resource evidence, and internal source conflicts should be handled."
          endpoint="db/ai-authoritative-resource-usage-spec/"
          queryKey={['ai-authoritative-resource-usage-spec']}
          fields={authoritativeFields}
          defaultValues={{
            key: 'default',
            name: '',
            instruction_template: '',
            conflict_handling: '',
            is_active: true,
          }}
          orderBy="key"
          rowTitle={(row) => row.name || row.key}
          rowSubtitle={(row) => safePreview(row.instruction_template, 180)}
          columns={[
            {
              key: 'name',
              label: 'Spec',
              width: 240,
              render: (row) => (
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>
                    {row.name || row.key}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {row.key}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'instruction_template',
              label: 'Instruction Template',
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.instruction_template, 260)}
                </Typography>
              ),
            },
            {
              key: 'conflict_handling',
              label: 'Conflict Handling',
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.conflict_handling, 220)}
                </Typography>
              ),
            },
            {
              key: 'is_active',
              label: 'Active',
              width: 100,
              render: (row) => boolChip(row.is_active),
            },
          ]}
        />
      ) : null}

      {tab === 'product-reference' ? (
        <TuningAdminTable
          title="Product Reference Instructions"
          description="Controls how product/context names are referenced in generated course sections."
          endpoint="db/ai-product-reference-instruction-spec/"
          queryKey={['ai-product-reference-instruction-spec']}
          fields={productReferenceFields}
          defaultValues={{
            key: 'default',
            name: '',
            instruction_template: '',
            is_active: true,
          }}
          orderBy="key"
          rowTitle={(row) => row.name || row.key}
          rowSubtitle={(row) => safePreview(row.instruction_template, 180)}
          columns={[
            {
              key: 'name',
              label: 'Spec',
              width: 240,
              render: (row) => (
                <Stack spacing={0.25}>
                  <Typography variant="body2" fontWeight={700}>
                    {row.name || row.key}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    {row.key}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'instruction_template',
              label: 'Instruction Template',
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.instruction_template, 320)}
                </Typography>
              ),
            },
            {
              key: 'is_active',
              label: 'Active',
              width: 100,
              render: (row) => boolChip(row.is_active),
            },
          ]}
        />
      ) : null}

      {tab === 'discovery' ? (
        <TuningAdminTable
          title="Discovery Generation Specs"
          description="DB-backed prompt/config specs used by discovery generation to create product components, product use cases, and other child entities without hardcoded prompts."
          endpoint="db/ai-discovery-generation-spec/"
          queryKey={['ai-discovery-generation-spec']}
          fields={discoveryGenerationFields}
          defaultValues={{
            target_type: 'product_component',
            parent_entity_type: 'product',
            name: '',
            description: '',
            prompt_template: '',
            guidelines: '',
            json_contract: {},
            min_items: 3,
            max_items: 8,
            sort_order: 100,
            is_active: true,
            updated_by: 'admin-ui',
          }}
          orderBy="parent_entity_type"
          rowTitle={(row) => row.name || `${row.parent_entity_type} → ${row.target_type}`}
          rowSubtitle={(row) => safePreview(row.prompt_template || row.description, 180)}
          columns={[
            {
              key: 'target',
              label: 'Discovery Target',
              render: (row) => (
                <Stack spacing={0.5}>
                  <Typography variant="body2" fontWeight={700}>
                    {row.name || `${row.parent_entity_type} → ${row.target_type}`}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Parent: {row.parent_entity_type || '-'}
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Target: {row.target_type || '-'}
                  </Typography>
                </Stack>
              ),
            },
            {
              key: 'bounds',
              label: 'Item Bounds',
              width: 150,
              sortable: false,
              render: (row) => (
                <Stack spacing={0.5}>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Min ${row.min_items ?? '-'}`}
                  />

                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Max ${row.max_items ?? '-'}`}
                  />
                </Stack>
              ),
            },
            {
              key: 'prompt_template',
              label: 'Prompt',
              sortable: false,
              render: (row) => (
                <Typography variant="caption" sx={{ whiteSpace: 'pre-wrap' }}>
                  {safePreview(row.prompt_template, 260)}
                </Typography>
              ),
            },
            {
              key: 'sort_order',
              label: 'Order',
              width: 90,
              render: (row) => row.sort_order,
            },
            {
              key: 'is_active',
              label: 'Active',
              width: 100,
              render: (row) => boolChip(row.is_active),
            },
          ]}
        />
      ) : null}
    </Box>
  );
};

export default PromptSpecsPanel;